import { Button, Menu, MenuItem, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useState } from 'react';
import MoreVert from '@mui/icons-material/MoreVert';
import { MoreHoriz } from '@mui/icons-material';
import { CustomToolbarItem } from 'shared/types/customToolbar';
import { ConfirmModal } from 'modules/common/Modals/ConfirmModal';

type ActionsButtonProps = {
  onOpen?: () => void;
  onClose?: () => void;
  actions: CustomToolbarItem[];
  disabled?: boolean;
  horizontal?: boolean;
  variant?: 'text' | 'outlined' | 'contained' | undefined;
};

const useStyles = makeStyles({
  root: {
    minWidth: 'unset',
    padding: '0.25rem',
  },
});

export const ActionsButton: FC<ActionsButtonProps> = ({
  actions,
  onOpen = () => null,
  onClose = () => null,
  disabled,
  horizontal,
  variant,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [itemForConfirm, setItemForConfirm] = useState<CustomToolbarItem | null>(null);

  const clearItemForConfirm = () =>
    /* Leaving `confirmation` to make sure title and text won't dissaper before the animation */
    setItemForConfirm(prev => ({ confirmation: prev?.confirmation } as CustomToolbarItem));

  const open = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    onOpen();
  };

  const close = () => {
    setAnchorEl(null);
    onClose();
  };

  const handleAction = (item: CustomToolbarItem) => () => {
    if (item.confirmation) {
      setItemForConfirm(item);
    } else {
      item.handler();
    }

    close();
  };

  const onConfirm = () => {
    itemForConfirm!.handler();
    clearItemForConfirm();
  };

  return (
    <>
      <Button
        className={classes.root}
        onClick={open}
        variant={variant ?? 'outlined'}
        disabled={disabled}
      >
        {horizontal ? <MoreHoriz /> : <MoreVert />}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={close}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {actions
          .filter(item => !item.hidden)
          .map((item: CustomToolbarItem) => (
            <Tooltip title={item.tooltip || ''} placement="top-end" key={item.text}>
              <span>
                <MenuItem
                  onClick={handleAction(item)}
                  disabled={item.disabled}
                  style={{ color: item.color }}
                >
                  {item.text}
                </MenuItem>
              </span>
            </Tooltip>
          ))}
      </Menu>
      <ConfirmModal
        open={!!itemForConfirm?.handler}
        onClose={clearItemForConfirm}
        onSuccess={onConfirm}
        title={itemForConfirm?.confirmation?.title!}
        text={itemForConfirm?.confirmation?.text!}
      />
    </>
  );
};
